import { getPlaceDetails, getPlacesPredictions } from '@services/google/places';
import { DEFINITIONS } from '../utils/constants';
import { refreshToken } from '../utils/token';
import { getTranslations } from '../utils/translations';

/**
 * @description Initializes the autocomplete functionality on the given context (container element).
 * Sets up event listeners for input changes, selection, and keyboard navigation.
 * @param {HTMLElement} context - The container element for the autocomplete UI.
 * @param {Object} options - Configuration options.
 * @param {Object} options.loader - The Google Maps API loader.
 * @param {Object} options.sessionToken - The session token for autocomplete requests.
 * @param {Object} options.map - The Google Map instance.
 * @param {Object} options.mapOptions - Options for the map (language, region).
 * @param {Object} options.userPosition - The user's current position (latitude, longitude).
 * @param {Function} callback - A callback function to be called when a place is selected.
 * @returns {Promise<void>}
 */
export const initAutocomplete = async (
  context,
  { loader, map, mapOptions, userPosition, autocompleteOptions },
  callback,
) => {
  const searchInput = context.querySelector(`.autocomplete__input`);
  if (!searchInput) return;

  const { AutocompleteSessionToken, AutocompleteSuggestion } = await loader.importLibrary('places');
  if (!AutocompleteSessionToken || !AutocompleteSuggestion)
    return console.error('Google Maps Places library is not loaded');

  let sessionToken = new AutocompleteSessionToken();

  // Instantiate to ensure the library side-effects are ready
  new AutocompleteSuggestion();

  const translations = getTranslations(context);

  const clearButton = context.querySelector('.autocomplete__clear');
  if (clearButton) {
    clearButton.addEventListener('click', () => {
      searchInput.value = '';
      const resultsContainer = context.querySelector('.autocomplete__results');
      if (!resultsContainer) return;
      const resultsList = resultsContainer.querySelector('.autocomplete__results__list');
      if (!resultsList) return;
      resultsList.replaceChildren();
      resultsContainer.style.display = 'none';
    });
  }

  searchInput.addEventListener('input', (event) => {
    const query = event.target.value;
    const resultsContainer = context.querySelector('.autocomplete__results');
    const resultsList = resultsContainer?.querySelector('.autocomplete__results__list');

    if (!resultsContainer || !resultsList) return;

    if (query.length < 3) {
      resultsList.replaceChildren();
      resultsContainer.style.display = 'none';
      return;
    }

    // Ref. https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data?hl=it#AutocompleteRequest
    const request = {
      input: query,
      sessionToken,
      language: mapOptions?.language ?? 'en',
      region: mapOptions?.region ?? 'US',
      origin: userPosition.coords
        ? new window.google.maps.LatLng(userPosition.coords.lat, userPosition.coords.lng)
        : undefined,
    };

    getPlacesPredictions(request)
      .then((suggestions) => {
        resultsList.replaceChildren();
        suggestions.forEach((suggestion) => {
          const placePrediction = suggestion.placePrediction;
          const listItem = document.createElement('li');
          listItem.classList.add(`${DEFINITIONS.slug}__autocomplete-result-item`, 'autocomplete__results__list__item');
          listItem.dataset.placeId = placePrediction.placeId;
          listItem.appendChild(document.createTextNode(placePrediction.text.text));

          listItem.addEventListener('click', () => {
            searchInput.value = placePrediction.text.text;
            listItem.classList.add(DEFINITIONS.activeClass);

            if (placePrediction.placeId) {
              getPlaceDetails({ id: placePrediction.placeId, fields: ['location'] })
                .then((place) => {
                  if (place && place.location) {
                    map.setCenter(place.location);
                    map.setZoom(autocompleteOptions?.zoom || 13);

                    if (typeof callback === 'function') {
                      callback(place);
                    }
                  }
                })
                .catch(console.error);
            }

            refreshToken(request, AutocompleteSessionToken);
          });

          resultsList.appendChild(listItem);
        });

        if (suggestions.length === 0)
          resultsList.innerHTML = `<li class="autocomplete__results__list__item">${translations?.autocomplete?.no_results}</li>`;
        resultsContainer.style.display = 'block';
      })
      .catch((error) => {
        console.error('Error fetching place predictions:', error);
        resultsList.innerHTML = `<li class="autocomplete__results__list__item">${translations?.autocomplete?.error}</li>`;
        resultsContainer.style.display = 'block';
      });
  });

  // Handle focus/blur for showing/hiding results
  searchInput.addEventListener('focus', () => {
    const resultsContainer = context.querySelector('.autocomplete__results');
    if (resultsContainer) resultsContainer.style.display = 'block';
  });

  searchInput.addEventListener('blur', () => {
    setTimeout(() => {
      const resultsContainer = context.querySelector('.autocomplete__results');
      if (resultsContainer) resultsContainer.style.display = 'none';
    }, 200);
  });

  // Handle keyboard navigation for accessibility
  searchInput.addEventListener('keydown', (event) => {
    const resultsContainer = context.querySelector('.autocomplete__results');
    const resultsList = resultsContainer?.querySelector('.autocomplete__results__list');
    if (!resultsList) return;

    const items = resultsList.querySelectorAll('.autocomplete__results__list__item');
    let selectedIndex = Array.from(items).findIndex((item) => item.classList.contains(DEFINITIONS.activeClass));

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      selectedIndex = (selectedIndex + 1) % items.length;
      items.forEach((item, index) => item.classList.toggle(DEFINITIONS.activeClass, index === selectedIndex));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      selectedIndex = (selectedIndex - 1 + items.length) % items.length;
      items.forEach((item, index) => item.classList.toggle(DEFINITIONS.activeClass, index === selectedIndex));
    } else if (event.key === 'Enter' && selectedIndex >= 0) {
      event.preventDefault();
      items[selectedIndex].dispatchEvent(new Event('click', { bubbles: true }));
    }
  });
};
